/* ── 194 §B.3 — THE SECTION IS THE CONFIGURATION; THE DRAWER IS WHERE RESULTS LIVE ──
 *
 * Laurent, 2026-09-18, ruling the shape after four prod measurements: "rework
 * its coherence instead of piecemeal approach leading to current situation" —
 * two screens became one workbench with two shells. This is the first shell:
 * the E-mails pane of the Communication section holds the two search rows
 * (EmailSearchRows, the ONE component the drawer renders too) and nothing
 * else. Either Search opens the drawer with that configuration, which runs the
 * search and shows the results (§C). The pane's own list of results — the
 * rows, the filter row, the billing pane, the save flow, the attachment modal,
 * the matter choice, some 1,800 lines that mirrored the drawer's "for parity"
 * (155 §4's AUTHORITY note) — is gone: the drawer already had every one of
 * those acts, and two copies of a list were the drift every brief since 171
 * paid for.
 *
 * WHAT THIS PANE STILL OWNS: the mailbox connection's state (the buttons are
 * disabled without one, as before), the served party list (B.1b — fetched
 * once per matter, handed to the rows and to the drawer), the configuration
 * (every party, the last ten days on both rows — B.1d), a refusal line (a
 * date box the browser cannot read, 192 D.2), and the drawer's mount with the
 * matter's own gates. The drawer is lazy-loaded, as it always was, so the case
 * page does not carry it until a Search is pressed.
 *
 * `import React` is here for `react/react-in-jsx-scope` (166's remedy).
 */
import React from "react";
import { useState, useEffect, lazy, Suspense } from "react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions";
import { FS } from "../lib/tokens";
import { PANE_TITLE, PANE_BODY } from "./CaseDocumentsSection"; // 196 §B.1 — the Documents pane's own dress, imported
import EmailSearchRows, { defaultEmailSearchConfig } from "./EmailSearchRows";
import type { EmailSearchConfig, EmailSearchKind, EmailSearchParty } from "./EmailSearchRows";
import { emailLogTimePrefill } from "../lib/emailLogTimePrefill"; // 184 C.1
import { storedCaseLanguage } from "../lib/docLang.mjs"; // 209 §6 — the matter's language, for its time entry's words
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad"; // 211 §10 — the parties, when they could not load
import { usePageList, usePageListContext, useReadMark, oneRow } from "../hooks/usePageList";
import { ListNotLoaded } from "./SharedUI"; // 212 §22 — the Microsoft 365 status, when it could not be read

// Drawer is lazy-loaded so the inline email pane — which renders on every
// case page — doesn't pull the drawer (iframe + preview pane + extra UI)
// into the main bundle. Loaded on demand the first time a Search is pressed.
const EmailDeepSearchDrawer = lazy(() => import("./EmailDeepSearchDrawer"));

/* 211 §10 — the served party list, asked the 210 §1 way (usePageList): the
   answer's `parties`, and an answer without them is a failure, never « 0 ». */
const readParties = (path: string): Promise<unknown> =>
  api<{ parties?: EmailSearchParty[] }>(path).then((d) => d?.parties);

/**
 * Props:
 *  cs: the case or collective-case object (must have .id)
 *  currentUser: collaborator id
 *  co: collaborators array (the viewer's own addresses, for reply-all)
 *  kind: 'case' (default) or 'cc'
 *  toast: toast fn
 *  openLogTime: opens the time modal with a prefill (184 C.2, both kinds)
 *  hideTitle: the host renders the pane's heading itself
 */
export default function CaseEmailsSection({
  cs,
  currentUser,
  co,
  kind = "case",
  toast,
  openLogTime,
  hideTitle = false,
  firmSettings = null, // 198 §C — the period pickers' axis input (D.3), the rows' and the drawer's
}: any) {
  /* 181 A.3 — `t` narrowed at the seam (the house pattern, InvoiceTable.tsx:137). */
  const { t, tIn } = useTranslation() as {
    t: (...a: [key: string, vars?: Record<string, unknown>]) => string;
    tIn: (...a: [lng: string, key: string, vars?: Record<string, unknown>]) => string; // 209 §6
  };
  const { can } = usePermissions();
  const scopeArg = kind === "cc" ? { ccId: cs.id } : { caseId: cs.id };
  // D16a — the Save column follows the Phase 13 caseView.emails.archive key;
  // Phase 21 AUTH — Bill follows the stand-alone caseView.emails.billing key.
  const canArchive = can("caseView.emails.archive", scopeArg);
  const canBill = can("caseView.emails.billing", scopeArg);

  const urlBase =
    kind === "cc"
      ? `/collective-cases/${cs.id}/emails`
      : `/cases/${cs.id}/emails`;

  const [m365, setM365] = useState<{ connected: boolean | null }>({ connected: null });
  /* 194 B.1b — the served party list: every party address of the matter with
     the party's name and role, the population the parties button picks from.
     211 §10 — it answered `[]` when it failed, and the quick row read
     « 0 parties »: asked the 210 §1 way now, and the row says it could not
     load, with « Réessayer ». */
  const _parties = usePageList<EmailSearchParty>(PAGE_LISTS.emailParties, "the e-mail parties", `${urlBase}/parties`, readParties);
  const parties = _parties.rows;
  /* 212 §22 — a status that could not be read is not « not connected » (it read
     so until now): asked the 210 §1 way, still failing it says « État
     Microsoft 365 inconnu » with « Réessayer » [ADVISOR], and the search stays
     open — its own answer says whether Microsoft 365 is connected. */
  const { entry: msRead, again: msAgain, ask: askMs } = useReadMark(PAGE_LISTS.m365Status);
  const _partyLists = usePageListContext(_parties.entry, msRead);
  /* 194 §B — the configuration: every party, the last ten days on both rows,
     no text, the completion off. Not persisted (the pre-194 period was not). */
  const [cfg, setCfg] = useState<EmailSearchConfig>(() => defaultEmailSearchConfig(undefined, firmSettings));
  const [error, setError] = useState<string | null>(null);
  /* the drawer, opened by a Search with the configuration and the row that
     asked; `nonce` remounts it per press so it opens fresh on that search */
  const [drawer, setDrawer] = useState<{ config: EmailSearchConfig; kind: EmailSearchKind; nonce: number } | null>(null);
  /* 197 §A [RULED — Laurent, 2026-09-19] — the row whose search made what the
     drawer shows wears amber HERE too (the pane and the drawer read as one
     thing): the kind that opened the drawer, then whatever it last ran
     (`onSearched`), and nothing once the drawer is closed — with it closed
     nothing on screen came from either row. */
  const [madeBy, setMadeBy] = useState<EmailSearchKind | null>(null);

  useEffect(() => {
    let live = true;
    void askMs("the Microsoft 365 status", () => oneRow(api<{ connected?: boolean }>("/auth/microsoft/status"))).then((rows) => {
      if (!live) return;
      const d = rows ? (rows[0] as { connected?: boolean } | null) : null;
      setM365({ connected: rows ? d?.connected === true : null });
    });
    return () => {
      live = false;
    };
  }, [urlBase, msAgain, askMs]);

  // 31.2 (D4) — the viewer's own address(es), excluded from Button B's
  // recipient superset (reply-all never re-mails yourself).
  const selfEmails = (() => {
    const me = (co || []).find((c: any) => Number(c.id) === Number(currentUser));
    return [me?.email, me?.m365Email].filter(Boolean);
  })();

  return (
    /* 196 §B.1 [RULED, Laurent 2026-09-19] — THE PANE WEARS THE DOCUMENTS
       SECTION'S DRESS: `.detail-card` + `<h4 style={PANE_TITLE}>` +
       `<div style={PANE_BODY}>`, CaseDocumentsSection's own idiom imported
       (« DOSSIER DE LA CAUSE »'s capitals and size); the inline card and the
       bare `<h3>` that stood here were this screen's own spacing. */
    <div className="detail-card">
      {!hideTitle && (
        <h4 style={PANE_TITLE}>
          {/* 194 B.0 — the first pane of the Communication section. */}
          {t("communication.pane_emails", { fallback: "E-mails" })}
        </h4>
      )}
      <div style={PANE_BODY}>
        {/* 194 §B.1 / §B.2 — the two rows; either Search opens the drawer (§B.3). */}
        <ListLoadContext.Provider value={_partyLists}>
          <EmailSearchRows
            t={t}
            value={cfg}
            onChange={setCfg}
            firmSettings={firmSettings}
            parties={parties}
            onSearch={(k) => {
              setError(null);
              setMadeBy(k);
              setDrawer({ config: cfg, kind: k, nonce: Date.now() });
            }}
            onRefuse={setError}
            disabled={m365.connected === false}
            activeKind={drawer ? madeBy : null}
          />

          {msRead.failure ? (
            <div style={{ margin: "6px 0 0" }}>
              <ListNotLoaded lists={[PAGE_LISTS.m365Status]} variant="inline" words={t("caseEmails.m365_status_unknown", { fallback: "Microsoft 365 status unknown" })} />
            </div>
          ) : (
            m365.connected === false && (
              <p style={{ fontSize: FS.s11, color: "var(--am)", margin: "6px 0 0" }}>
                {t("caseEmails.m365_not_connected", {
                  fallback:
                    "⚠ Microsoft 365 not connected. Connect in Settings to search emails.",
                })}
              </p>
            )
          )}
        </ListLoadContext.Provider>
        {error && <p style={{ fontSize: FS.s11, color: "var(--rd)", margin: "6px 0 0" }}>{error}</p>}

        {drawer && (
          <Suspense fallback={null}>
            <EmailDeepSearchDrawer
              key={drawer.nonce}
              open
              onClose={() => {
                setDrawer(null);
                setMadeBy(null);
              }}
              urlBase={urlBase}
              caseRef={cs?.ref}
              partyList={parties}
              initialConfig={drawer.config}
              initialKind={drawer.kind}
              firmSettings={firmSettings}
              onSearched={setMadeBy}
              toast={toast}
              canBill={canBill}
              canArchive={canArchive}
              selfEmails={selfEmails}
              // 184 C.2 [RULED (iii)] — "+ Add time entry on selected" works for
              // BOTH kinds: the prefill is `emailLogTimePrefill` (C.1), keyed
              // `caId` or `ccId` by the kind, and the gate is the opener alone.
              canLogTime={!!openLogTime}
              onLogTime={(msg) => {
                if (!openLogTime || !cs?.id) return;
                // 209 §6 — an e-mail with no subject is named in the MATTER's language (none: left to type)
                const matterLang = storedCaseLanguage(cs);
                openLogTime(
                  emailLogTimePrefill(msg, { kind: kind === "cc" ? "cc" : "case", id: cs.id }, currentUser,
                    matterLang ? tIn(matterLang, "email.no_subject", { fallback: "(no subject)" }) : null),
                );
                setDrawer(null);
              }}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}
